<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useBrandingStore } from '@/stores/brandingStore'
import { useGlobalStore } from '@/stores/globalStore'
import { useUIStore } from '@/stores/uiStore'
import NotificationsBell from '@/layouts/partials/NotificationsBell.vue'
import UserMenu from '@/layouts/partials/UserMenu.vue'

const globalStore = useGlobalStore()
const uiStore = useUIStore()
const brandingStore = useBrandingStore()
const { t } = useI18n()

const appName = import.meta.env.VITE_APP_NAME ?? 'HonestWaves'

const themeToggleLabel = computed(() =>
  globalStore.currentTheme === 'dark' ? t('header.switchToLight') : t('header.switchToDark'),
)

const toggleTheme = (): void => {
  globalStore.setTheme(globalStore.currentTheme === 'dark' ? 'light' : 'dark')
}

/**
 * Header brand mark — resolved from the live branding store with
 * a four-tier cascade (highest priority first):
 *
 *   1. **Uploaded site logo** — operator-supplied LOGO asset from
 *      Configs → Branding. When present, that's the mark; theme is
 *      irrelevant (operator's brand is what it is).
 *   2. **Company name** — when no upload but the tenant has set
 *      a `company_name`, render the text mark. Takes precedence
 *      over the default logo because a typed name is a STRONGER
 *      brand signal than our generic placeholder wordmark.
 *   3. **Default logo** — the shipped
 *      `/storage/brand/logo-horizontal-*` wordmark (theme-flipped
 *      via `<picture>`+WebP+PNG fallback). Lives in
 *      `backend/storage/app/public/brand/` and is served at
 *      `/storage/brand/*` via the storage.conf nginx snippet
 *      (Laravel-aligned `/storage/` URL convention).
 *   4. **App name text** — last-resort fallback when even the
 *      default logo fails to load (fresh checkout before brand
 *      assets ship; nginx hasn't picked up the storage mount).
 *
 * Mobile compact mark uses APP_ICON in place of LOGO with the
 * same cascade (uploaded → company name → default
 * `/storage/brand/icon.*` → text).
 *
 * The store mounts the data ONCE on Header mount via
 * `ensureLoaded()` (idempotent — concurrent callers share the
 * in-flight request). PATCH / upload / delete responses
 * dispatched from ConfigsBrandingSection write through
 * `brandingStore.applyState(...)`, which re-renders this header
 * live without a page reload.
 */
onMounted(() => void brandingStore.ensureLoaded())

const uploadedLogo = computed<string | null>(() => {
  const slot = brandingStore.asset('logo')
  return slot && slot.is_default === false ? slot.url : null
})
const uploadedIcon = computed<string | null>(() => {
  const slot = brandingStore.asset('app_icon')
  return slot && slot.is_default === false ? slot.url : null
})
const companyName = computed<string | null>(() => brandingStore.companyName)

/** Default (theme-flipped) wordmark + compact icon paths. */
const logoBase = computed<string>(() =>
  globalStore.currentTheme === 'dark'
    ? '/storage/brand/logo-horizontal-dark'
    : '/storage/brand/logo-horizontal-light',
)
const logoWebp = computed<string>(() => `${logoBase.value}.webp`)
const logoPng = computed<string>(() => `${logoBase.value}.png`)
const iconWebp = '/storage/brand/icon.webp'
const iconPng = '/storage/brand/icon.png'

const logoLoaded = ref<boolean>(true)
const iconLoaded = ref<boolean>(true)
const onLogoError = (): void => { logoLoaded.value = false }
const onIconError = (): void => { iconLoaded.value = false }

// NOTE: header-level "access lookup" (cross-resource search by badge
// / device SN / kiosk serial) is hidden in the template below until
// the lookup endpoint lands. The input v-model + ref are removed
// alongside the template block; restore both when re-enabling.
</script>

<template>
  <header
    class="sticky top-0 z-30 h-14 px-4 flex items-center gap-3 border-b border-border"
  >
    <!-- Hamburger — viewport-aware via `ui.toggleSidebar()`:
           `<md` (mobile) opens / closes the drawer overlay
           `md+`  (desktop) toggles compact (icons-only) mode
         Always visible so operators have a CHROME-LEVEL toggle
         alongside the subtle chevron decorator on the divider.
    -->
    <Button
      icon="pi pi-bars"
      severity="secondary"
      text
      rounded
      :aria-label="t('header.toggleSidebar')"
      @click="uiStore.toggleSidebar()"
    />

    <!--
      Brand mark — horizontal logo on `sm+`, compact icon on
      mobile. Each viewport has its own image-or-text swap so a
      missing asset on ONE viewport doesn't blank out the other.
      Falls back to plain `appName` text when the image asset
      fails to load (`/storage/brand/*` missing in some envs,
      e.g. fresh checkout before assets land or nginx not yet
      restarted to pick up the storage volume mount).
    -->
    <a
      href="/"
      class="flex items-center gap-2 min-w-0 no-underline"
      :aria-label="companyName ?? appName"
    >
      <!--
        Desktop / tablet — cascade (highest priority first):
          1. Uploaded site logo  → operator-supplied image
          2. Company name        → typed text
          3. Default logo        → theme-flipped /storage/brand/logo-horizontal-*
                                  (WebP → PNG → text-fallback chain)
          4. App name            → Vite env / 'HonestWaves'
      -->
      <div class="hidden sm:block">
        <img
          v-if="uploadedLogo"
          :src="uploadedLogo"
          :alt="companyName ?? appName"
          class="h-7 w-auto"
        />
        <h1
          v-else-if="companyName"
          class="m-0 text-lg font-semibold text-color truncate"
        >
          {{ companyName }}
        </h1>
        <picture v-else-if="logoLoaded">
          <source :srcset="logoWebp" type="image/webp" />
          <img
            :src="logoPng"
            :alt="appName"
            class="h-7 w-auto"
            @error="onLogoError"
          />
        </picture>
        <h1 v-else class="m-0 text-lg font-semibold text-color truncate">
          {{ appName }}
        </h1>
      </div>
      <!--
        Mobile — same cascade, compact mark instead of wordmark:
        uploaded APP_ICON → company name → default /storage/brand/icon.*
        → app name text.
      -->
      <div class="sm:hidden">
        <img
          v-if="uploadedIcon"
          :src="uploadedIcon"
          :alt="companyName ?? appName"
          class="h-7 w-7"
        />
        <h1
          v-else-if="companyName"
          class="m-0 text-lg font-semibold text-color truncate"
        >
          {{ companyName }}
        </h1>
        <picture v-else-if="iconLoaded">
          <source :srcset="iconWebp" type="image/webp" />
          <img
            :src="iconPng"
            :alt="appName"
            class="h-7 w-7"
            @error="onIconError"
          />
        </picture>
        <h1 v-else class="m-0 text-lg font-semibold text-color truncate">
          {{ appName }}
        </h1>
      </div>
    </a>

    <!-- Spacer pushes the trailing cluster (search, theme, user) to
         the right edge. -->
    <div class="flex-1" />

    <!--
      Access lookup — HIDDEN until the global cross-resource lookup
      endpoint lands. Restore the IconField/InputText block + the
      `accessLookup` ref in <script setup> together.
    -->

    <NotificationsBell />

    <Button
      :icon="globalStore.currentTheme === 'dark' ? 'pi pi-sun' : 'pi pi-moon'"
      severity="secondary"
      text
      rounded
      :aria-label="themeToggleLabel"
      @click="toggleTheme"
    />

    <UserMenu />
  </header>
</template>
